// eslint-disable-next-line import/no-cycle
// eslint-disable-next-line import/no-cycle
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { createContext, useContext, useMemo, useState, useCallback, memo } from 'react';

import classnames from 'classnames';

import { useMediaQuery } from '@mui/material';

import Typography from 'components/uiLibrary/Typography';

// eslint-disable-next-line import/no-cycle
import FilterFacets, { FILTER_LAYOUT } from 'components/Filters/Display/FilterFacets';
import CalendarSlug from 'components/Productions/Display/Slugs/CalendarSlug';
import Actions from 'components/Productions/Display/Slugs/ProductionPublicSlug/Actions/Actions';

import { CALENDAR_VIEW_DAYS, CALENDAR_VIEW_TYPES } from 'constants/calendar';
import { DATE_FORMATS, TP } from 'constants/index';

import { getViewRange } from 'utils/calendar';
import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import usePageContext from 'utils/hooks/usePageContext';
import useSharedCalendarRange from 'utils/hooks/useSharedCalendarRange';
import { groupVenuesByLocation } from 'utils/productions';

import { useTranslation } from 'src/i18n';

import { ROUTE_RESERVED_KEYWORDS, FILTER_TYPES, ENTITY_TYPES } from 'operabase-router/constants';

import CalendarHeaders from './Calendar/CalendarHeaders';
import CalendarLegends from './Calendar/CalendarLegends';
import CalendarRangeNavigation from './Calendar/CalendarRangeNavigation';
import Cells from './Calendar/Cells';
import classes from './CalendarProductionListing.module.scss';

const CalendarViewContext = createContext();

const EntityAndDateSelected = memo(({ entity, rangeStart, rangeEnd }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  return (
    <div className={classes.entitySelectedDates}>
      <div className={classes.entitySelectedDates__left}>
        <Typography size={12} className={classes.text}>
          {t(`${TP}.JUMPIN_DATES_SELECTED_BY_YOU`)}
          <span className={classes.dateSelectedBox}></span>
        </Typography>
        <Typography size={12} className={classes.text}>
          {t(`${TP}.ENTITY_NAME_SCHEDULE_PERFORMANCE_DATES`, { entityName: entity?.name })}
          <span className={classes.performedDatesBox}></span>
        </Typography>
      </div>
      <Typography color="secondary" className={classes.dateSelected}>
        {rangeStart} - {rangeEnd}
      </Typography>
    </div>
  );
});

export const CalendarViewSlug = memo(({ entityType, entity, data: production, openQuickPreview, trackingData }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const isSmallScreen = useMediaQuery('(max-width:1200px)');
  const [isExpanded, setIsExpanded] = useState(false);
  const { viewType, rangeStartDate } = useContext(CalendarViewContext);
  const { baseRoute } = usePageContext();
  const { obRouteContext } = useAppContext();
  const { filters: linkFilters } = obRouteContext?.linkProps || {};
  const { [FILTER_TYPES.SCORING_PARAMS]: scoringParams } = linkFilters || {};
  const isCastingToolPage = scoringParams?.some(item => item?.entity?.name === FILTER_TYPES.AVAILABILITY);
  const isCastingRoute = baseRoute === ROUTE_RESERVED_KEYWORDS.casting;
  const isCastingProfile =
    isCastingRoute && obRouteContext.page?.entityType === ENTITY_TYPES.PROFILE && !!obRouteContext.page?.entity?.id;
  const isToolCastingPage = isCastingRoute && !isCastingProfile;

  const { months } = useMemo(() => getViewRange({ viewType, rangeStartDate }) || [], [viewType, rangeStartDate]);

  const handleExpandSlug = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    },
    [isExpanded],
  );

  const entityPrefDates = useMemo(() => {
    if (!production?.contributions || !entity?.id) {
      return [];
    }

    const profilePerformances = production.contributions.find(item => item?.profile?.id === entity.id)?.performances;
    return (
      profilePerformances?.map((d, i) => ({
        startDate: d,
        id: i + 1,
        isCancelled: d?.isCancelled,
        startTime: d?.startTime,
      })) || []
    );
  }, [production?.contributions, entity?.id]);

  const handlePerformanceClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      openQuickPreview(e, { productionId: production?.id });
    },
    [openQuickPreview, production?.id],
  );

  const venuesDates = useMemo(() => groupVenuesByLocation(production), [production]);
  const entityContribution = useMemo(
    () => production?.contributions?.find(contribution => contribution?.profile?.id === entity?.id)?.credit || null,
    [production?.contributions, entity?.id],
  );

  const [primaryVenue, ...restVenues] = venuesDates || [];

  const cityCountry = useMemo(() => {
    if (!venuesDates || venuesDates.length === 0) {
      return { cityText: '', countryText: '' };
    }

    const uniqueCities = [...new Set(venuesDates.map(venue => venue.city).filter(Boolean))];
    const uniqueCountries = [...new Set(venuesDates.map(venue => venue.country).filter(Boolean))];

    const totalCities = uniqueCities.length;
    const totalCountries = uniqueCountries.length;

    if (totalCountries === 0) {
      if (totalCities === 0) {
        return { cityText: '', countryText: '' };
      }

      if (totalCities === 1) {
        return { cityText: uniqueCities[0], countryText: '' };
      }

      return { cityText: `+${totalCities} ${t(`${TP}.FN_CITIES`)}`, countryText: '' };
    }

    if (totalCountries === 1) {
      const country = uniqueCountries[0];

      if (totalCities === 1) {
        return { cityText: uniqueCities[0], countryText: country };
      }

      return { cityText: `+${totalCities} ${t(`${TP}.FN_CITIES`)}`, countryText: country };
    }

    if (totalCities === 1) {
      return { cityText: uniqueCities[0], countryText: `+${totalCountries} ${t(`${TP}.COUNTRIES`)}` };
    }

    return {
      cityText: `+${totalCities} ${t(`${TP}.FN_CITIES`)}`,
      countryText: `+${totalCountries} ${t(`${TP}.COUNTRIES`)}`,
    };
  }, [venuesDates, t]);

  return (
    <>
      <tr className={classes.slugTable__row} key={production?.id} onClick={handlePerformanceClick}>
        {!isSmallScreen && (
          <td className={classes.slugTable__row_yearFirst}>
            {production?.id ? (
              <CalendarSlug
                entity={entity}
                entityType={entityType}
                production={production}
                openQuickPreview={openQuickPreview}
                trackingData={trackingData}
                handleExpandSlug={handleExpandSlug}
                isProView={isCastingProfile}
                isCastingToolPage={isCastingToolPage}
                isExpanded={isExpanded}
                cityCountry={
                  isExpanded && primaryVenue
                    ? { cityText: primaryVenue?.city, countryText: primaryVenue?.country }
                    : cityCountry
                }
                styles={{
                  calendarSlug: classes.calendarSlug,
                }}
              />
            ) : (
              <Typography className={classes.noProductionText} color="secondary" italic size={12}>
                {t(`${TP}.NO_SCHEDULED_UPCOMING_PERFORMANCE`, { entityName: entity?.name })}
              </Typography>
            )}
          </td>
        )}
        <Cells
          production={production}
          viewType={viewType}
          rangeStartDate={rangeStartDate}
          months={months}
          entityPrefDates={entityPrefDates}
          isCastingView={isToolCastingPage}
        />
      </tr>
      {isExpanded &&
        restVenues?.map((item, i) => (
          <tr
            className={classnames(classes.slugTable__rowExpanded, {
              [classes.rowHighlight]: isExpanded,
              [classes.lastRow]: i === restVenues.length - 1,
            })}
            key={`${production?.id}-${i + 1}`}
            onClick={handlePerformanceClick}
          >
            <td className={classes.expandedRow}>
              <div className={classes.countryCityTd}>
                <Typography size={12} weight="medium">
                  {item?.city}
                  {item?.city && item?.country ? ',' : ''}
                </Typography>
                <Typography size={12} color="secondary">
                  {item?.country}
                </Typography>
              </div>
              <div className={classes.ticketBlock}>
                {isCastingProfile ? (
                  <Typography size={12} weight="bold" className={classes.entityContribution}>
                    {entityContribution}
                  </Typography>
                ) : (
                  <Actions data={production} entity={entity} entityType={entityType} variant="textAndBlock" />
                )}
              </div>
            </td>
            <Cells
              production={production}
              viewType={viewType}
              rangeStartDate={rangeStartDate}
              months={months}
              performedDates={item?.performances}
            />
          </tr>
        ))}
    </>
  );
});

const CalendarProductionListing = ({ rows, hideFilters = false, entity, hideRangeNavigation, minDate, maxDate }) => {
  const {
    viewType,
    rangeStartDate,
    rangeEndDate,
    onShiftLeft,
    onShiftRight,
    allowLeftShift,
    allowRightShift,
    jumpInHighlightDates,
  } = useSharedCalendarRange(minDate, maxDate);
  const { obRouteContext } = useAppContext();
  const { linkProps } = obRouteContext || {};
  const { filters: linkFilters } = linkProps || {};
  const { [FILTER_TYPES.SCORING_PARAMS]: scoringParams } = linkFilters || {};
  const isCastingToolPage = scoringParams?.some(item => item?.entity?.name === FILTER_TYPES.AVAILABILITY);
  const { baseRoute } = usePageContext();
  const isCastingProfile =
    obRouteContext.page?.entityType === ENTITY_TYPES.PROFILE && !!obRouteContext.page?.entity?.id;
  const isToolCastingPage = baseRoute === ROUTE_RESERVED_KEYWORDS.casting && !isCastingProfile;

  const isLaptop = useMediaQuery('(max-width:1280px)');
  const isSmallScreen = useMediaQuery('(max-width:1200px)');
  const customFilterGroups = useMemo(() => {
    let filters = [
      { name: `${TP}.FN_GENRE`, filterTypes: [FILTER_TYPES.CREATOR] },
      { name: `${TP}.m_PROD`, filterTypes: [FILTER_TYPES.WORK] },
      { name: `${TP}.FN_PRODUCER`, filterTypes: [FILTER_TYPES.PRODUCER] },
      { name: `${TP}.WHO`, filterTypes: [FILTER_TYPES.WHO] },
      { name: `${TP}.WHERE`, filterTypes: [FILTER_TYPES.WHERE] },
      {
        name: isCastingProfile ? `${TP}.FN_ROLE_TITLE` : `${TP}.FN_ALL`,
        filterTypes: isCastingProfile ? [FILTER_TYPES.ROLE] : [FILTER_TYPES.TICKET],
      },
    ];

    if (isLaptop) {
      const excludedTypes = [FILTER_TYPES.ROLE, FILTER_TYPES.TICKET];

      filters = filters.filter(({ filterTypes }) => !excludedTypes.includes(filterTypes[0]));
    }

    if (isSmallScreen) {
      const excludedTypes = [FILTER_TYPES.WHO];

      filters = filters.filter(({ filterTypes }) => !excludedTypes.includes(filterTypes[0]));
    }

    return {
      [FILTER_LAYOUT.BUTTON_GROUP_DETAILED]: filters,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLaptop, isSmallScreen]);

  const contextValue = useMemo(
    () => ({
      rangeStartDate,
      rangeEndDate,
      viewType,
      jumpInHighlightDates,
    }),
    [rangeStartDate, rangeEndDate, viewType, jumpInHighlightDates],
  );

  const formattedDateRange = useMemo(
    () => ({
      rangeStart: createDate(rangeStartDate).format(DATE_FORMATS.DATE_LABEL),
      rangeEnd: createDate(rangeEndDate).format(DATE_FORMATS.DATE_LABEL),
    }),
    [rangeStartDate, rangeEndDate],
  );

  return (
    <CalendarViewContext.Provider value={contextValue}>
      <table className={classes.slugTable}>
        <thead className={classes.slugTable__head}>
          <tr>
            <td className={classes.slugTable__head_filters}>
              {!hideFilters && (
                <FilterFacets
                  layout={FILTER_LAYOUT.BUTTON_GROUP_DETAILED}
                  customGroup={customFilterGroups}
                  ignoreSmallScreenGroup
                  styles={{
                    root: classes.filterContainerDetailed,
                    filterGroupBox: classes.filterGroupBox,
                  }}
                />
              )}
              <div className={classes.calendarLegends}>
                <CalendarLegends />
              </div>
            </td>
            {!hideRangeNavigation && !isSmallScreen && (
              <td colSpan={CALENDAR_VIEW_DAYS[viewType]}>
                <CalendarRangeNavigation
                  viewType={viewType}
                  startDate={rangeStartDate}
                  endDate={rangeEndDate}
                  onShiftLeft={onShiftLeft}
                  onShiftRight={onShiftRight}
                  allowLeftShift={allowLeftShift}
                  allowRightShift={allowRightShift}
                />
              </td>
            )}
          </tr>

          <tr className={classes.slugTable__head_row}>
            {entity?.id && !isSmallScreen && (
              <td>
                <EntityAndDateSelected
                  entity={entity}
                  rangeStart={formattedDateRange.rangeStart}
                  rangeEnd={formattedDateRange.rangeEnd}
                />
              </td>
            )}
            {[
              CALENDAR_VIEW_TYPES.YEAR,
              CALENDAR_VIEW_TYPES.MONTH,
              CALENDAR_VIEW_TYPES.FIFTEEN_DAYS,
              CALENDAR_VIEW_TYPES.QUARTER,
            ].includes(viewType) &&
              !isCastingToolPage && <th className={classes.yearMonthsHeader}></th>}
            <CalendarHeaders
              viewType={viewType}
              rangeStartDate={rangeStartDate}
              isCastingView={isToolCastingPage}
              highlightDates={jumpInHighlightDates}
            />
          </tr>
        </thead>

        <tbody>{rows}</tbody>
      </table>
    </CalendarViewContext.Provider>
  );
};

export default CalendarProductionListing;
